import { PlanColumn, PlanPeriod } from "modules/spending/workload-plan/models/plan-types";
import { Milestone } from "./milestones-store";

/**
 * Веха на шкале. column — индекс колонки среди ВСЕХ загруженных: ближайший рабочий день
 * не раньше даты вехи (веха на выходном встаёт на следующий рабочий день). -1 — раньше
 * начала шкалы, columns.length — позже конца; видимость по глубине показа решает страница.
 */
export interface PlanMilestone extends Milestone {
    column: number;
}

/** Вид участника проекта — совпадает с ProjectPlanRowKinds на бэке */
export type ProjectRowKind = "employer" | "outsourcer";

/** Отрезок работ в координатах колонок сетки */
export interface PlanSegment {
    /** Индекс первой видимой колонки */
    start: number;
    /** Видимая длина в колонках — сколько отрезка попало на экран */
    length: number;
    /**
     * Настоящая длина отрезка в рабочих днях, до обрезки по диапазону.
     * Именно её показываем: видимая длина — это не срок работ, а размер окна.
     */
    workDays: number;
    /** Настоящий последний день отрезка, "2028-12-15"; может лежать за краем сетки */
    stopDay: string;
    /** Отрезок начался левее видимой сетки */
    continuesLeft: boolean;
    /** Отрезок продолжается правее видимой сетки */
    continuesRight: boolean;
    /** Работа плана — за неё отрезок тянут в режиме правки; null у подрядчиков и полос */
    assignmentId: string | null;
    /** Прежнее положение работы в черновике: только контур, без жестов */
    ghost: boolean;
    /** Предложенное положение из запроса на согласование (id запроса): контур другого вида */
    proposed?: string | null;
    /**
     * Часть работы до сегодняшнего дня. Работа режется по текущей дате, как блоки
     * в Табеле План: прошлое видно, но не двигается — тянется только текущая часть
     */
    isPast: boolean;
    /** Текущая часть работы, начавшейся в прошлом: её левый край — сегодня, а не начало работы */
    afterPast?: boolean;
}

/** Строка графика: сотрудник или договор с подрядчиком */
export interface ProjectParticipant {
    kind: ProjectRowKind;
    id: string;
    /** ФИО сотрудника или наименование подрядчика */
    label: string;
    /** Должность сотрудника или номер договора подрядчика */
    note: string;
    /** Раздел состава, к которому привязан договор подрядчика — туда ведёт клик по строке */
    sectionId: string | null;
    segments: PlanSegment[];
}

/**
 * Раздел состава проекта (режим «Разделы»): полоса от первого до последнего дня
 * исполнителей и сами исполнители третьим уровнем.
 */
export interface ProjectPlanSectionView {
    /** guid раздела; пустая строка — «Без раздела» */
    id: string;
    /** Стадия — она же категория в «Составе проекта» */
    stage: string;
    /** Короткий шифр «КР3» */
    code: string;
    /** Полный шифр «49-26- КР3» */
    name: string;
    description: string;
    /** Ответственный, у аутсорса — подрядчик */
    note: string;
    outsourced: boolean;
    /** Раздел убран из состава проекта, а работы на нём остались */
    inactive: boolean;
    summary: PlanSegment | null;
    /**
     * Заданный срок раздела на сетке. Есть — полоса раздела рисуется по нему, нет —
     * срок равен работам (summary)
     */
    term: PlanSegment | null;
    /** Полоса раздела (срок или работы) по сегодняшнему дню: прошлая часть и текущая */
    parts: PlanSegment[];
    rows: ProjectParticipant[];
}

/** Детализация проекта: по сотрудникам или по разделам состава */
export type ProjectPlanDetail = "employees" | "sections";

/**
 * Оформление проекта. Считается один раз при сборке модели: цвет у всех полос
 * проекта общий, гонять hex2rgba/shadeColor на каждый рендер незачем.
 *
 * Яркостей две, и путать их нельзя. Полоса полупрозрачная и лежит на белой сетке,
 * поэтому её контраст считается по цвету, УЖЕ смешанному с белым. Плашка с номером
 * залита чистым цветом проекта — у неё своя яркость и свой цвет текста.
 */
export interface ProjectPaint {
    /** Заливка полосы — цвет проекта, смешанный с белым */
    background: string;
    /** Рамка полосы */
    borderColor: string;
    /** Заливка полосы тёмная — подпись белая, иначе тёмно-синяя */
    dark: boolean;
    /** Чистый цвет проекта — заливка плашки с номером */
    chipBackground: string;
    /** Плашка тёмная — номер белый, иначе тёмно-синий */
    chipDark: boolean;
    /**
     * Заливка вехи: цвет проекта, осветлённый на тёмном проекте и затемнённый на светлом —
     * чтобы центр вехи отделялся от полосы того же цвета
     */
    milestoneFill: string;
}

export interface ProjectPlanProject {
    id: string;
    name: string;
    color: string;
    inventoryNumber: number;
    fullInventoryNumber: string;
    orderNumber: string;
    status: string;
    statusColor: string;
    /** Главный инженер проекта, «Фамилия И. О.» */
    employer: string;
    /** Id ГИПа — ссылка в карточку сотрудника */
    employerId: string | null;
    /** Крайний день работ по проекту, "2026-09-11"; считается бэком по всем отрезкам */
    lastDay: string;
    paint: ProjectPaint;
    rows: ProjectParticipant[];
    /** Те же работы по разделам состава проекта */
    sections: ProjectPlanSectionView[];
    /** Все вехи проекта, включая лежащие за краями шкалы */
    milestones: PlanMilestone[];
    /** Текущий пользователь правит вехи проекта: правка графиков на все проекты или ГИП */
    canEditMilestones: boolean;
    /**
     * Сводная полоса — от самой ранней работы проекта до самой поздней.
     * Считается на сервере по ВСЕМ участникам, до фильтрации по отделу, и не
     * пересчитывается по видимым строкам: сколько бы их ни осталось после прав
     * и поиска, длина проекта показывается целиком.
     */
    summary: PlanSegment | null;
    /** Сводная полоса по сегодняшнему дню: прошлая часть (бледная) и текущая */
    summaryParts: PlanSegment[];
    /** Плановый срок проекта, yyyy-MM-dd; null — не задан */
    planEnd: string | null;
    /** Заданные сроки разделов по guid; раздела нет в списке — его срок равен работам */
    sectionTerms: { [guid: string]: { start: string | null; end: string | null } };
    /** Колонка засечки планового срока (см. planEndColumn в билдере); null — срок не задан */
    planEndColumn: number | null;
}

export interface ProjectPlanSnapshot {
    columns: PlanColumn[];
    periods: PlanPeriod[];
    projects: ProjectPlanProject[];
    /** Правка графика на все проекты */
    editAll: boolean;
    /** Сотрудники, чьи работы пользователь двигает как начальник отдела */
    editableEmployers: string[];
}

export const EMPTY_PROJECT_PLAN: ProjectPlanSnapshot = {
    columns: [],
    periods: [],
    projects: [],
    editAll: false,
    editableEmployers: [],
};

export type { PlanColumn, PlanPeriod };
